import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  TablePagination,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ResponsiveTable, Column } from '../../components/mobile/ResponsiveTable';
import AddIcon from '@mui/icons-material/Add';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PersonIcon from '@mui/icons-material/Person';
import StoreIcon from '@mui/icons-material/Store';
import RepeatIcon from '@mui/icons-material/Repeat';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import axiosInstance from '../../lib/axios';
import { TableToolbar, FilterOption, ColumnConfig } from '../../components/TableToolbar';
import type {
  Expense,
  ExpenseCategory,
  PaymentMethod,
  PaginatedResponse,
  Location,
} from '../../types';
import {
  ExpenseCategoryLabels,
  PaymentMethodLabels,
} from '../../types';
import { palette } from '../../theme/colors';

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-DZ', {
    style: 'currency',
    currency: 'DZD',
  }).format(amount);
};

const fetchExpenses = async (
  page: number,
  pageSize: number,
  filters: {
    search?: string;
    category?: string;
    locationId?: string;
    startDate?: string;
    endDate?: string;
    paymentMethod?: string;
  }
): Promise<PaginatedResponse<Expense>> => {
  const params: Record<string, any> = {
    page: page + 1,
    limit: pageSize,
  };

  if (filters.search) params.search = filters.search;
  if (filters.category) params.category = filters.category;
  if (filters.locationId) params.locationId = filters.locationId;
  if (filters.startDate) params.startDate = filters.startDate;
  if (filters.endDate) params.endDate = filters.endDate;
  if (filters.paymentMethod) params.paymentMethod = filters.paymentMethod;

  const response = await axiosInstance.get('/expenses', { params });

  return {
    data: Array.isArray(response.data) ? response.data : response.data.items || [],
    total: response.data.total || (Array.isArray(response.data) ? response.data.length : 0),
  };
};

const fetchLocations = async (): Promise<Location[]> => {
  const response = await axiosInstance.get('/locations');
  return Array.isArray(response.data) ? response.data : response.data.items || [];
};

const categoryColors: Record<ExpenseCategory, string> = {
  restock: palette.info,
  salary: palette.primary,
  consumable: palette.warning,
  utilities: palette.accent,
  rent: palette.error,
  maintenance: palette.success,
  transport: '#9c27b0',
  other: palette.textMuted,
};

const categoryIcons: Record<ExpenseCategory, React.ReactNode> = {
  restock: <ReceiptIcon sx={{ fontSize: 18 }} />,
  salary: <PersonIcon sx={{ fontSize: 18 }} />,
  consumable: <ReceiptLongIcon sx={{ fontSize: 18 }} />,
  utilities: <AttachMoneyIcon sx={{ fontSize: 18 }} />,
  rent: <StoreIcon sx={{ fontSize: 18 }} />,
  maintenance: <ReceiptLongIcon sx={{ fontSize: 18 }} />,
  transport: <ReceiptLongIcon sx={{ fontSize: 18 }} />,
  other: <ReceiptLongIcon sx={{ fontSize: 18 }} />,
};

const getExportColumns = (): ColumnConfig[] => [
  { field: 'expenseDate', headerName: 'Date' },
  {
    field: 'category',
    headerName: 'Catégorie',
    valueFormatter: (value) => ExpenseCategoryLabels[value as ExpenseCategory] || value,
  },
  { field: 'description', headerName: 'Description' },
  {
    field: 'amount',
    headerName: 'Montant',
    valueFormatter: (value) => formatCurrency(value as number),
  },
  {
    field: 'paymentMethod',
    headerName: 'Mode de paiement',
    valueFormatter: (value) => PaymentMethodLabels[value as PaymentMethod] || value,
  },
  { field: 'referenceNumber', headerName: 'Référence' },
];

interface ExpensesListProps {
  onCreateClick?: () => void;
  onExpenseClick?: (expense: Expense) => void;
  onGenerateSalariesClick?: () => void;
  onSalaryConfigClick?: () => void;
}

export const ExpensesList = ({
  onCreateClick,
  onExpenseClick,
  onGenerateSalariesClick,
  onSalaryConfigClick,
}: ExpensesListProps) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  // Fetch locations for filter dropdown
  const { data: locationsData } = useQuery({
    queryKey: ['locations'],
    queryFn: fetchLocations,
  });

  const filters = useMemo(
    () => ({
      search: searchValue,
      category: activeFilters.category,
      locationId: activeFilters.locationId,
      startDate: activeFilters.startDate,
      endDate: activeFilters.endDate,
      paymentMethod: activeFilters.paymentMethod,
    }),
    [searchValue, activeFilters]
  );

  const { data, isLoading } = useQuery({
    queryKey: ['expenses', page, pageSize, filters],
    queryFn: () => fetchExpenses(page, pageSize, filters),
  });

  const filteredData = useMemo(() => {
    return data?.data || [];
  }, [data?.data]);

  const handleFilterChange = (field: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleClearFilters = () => {
    setActiveFilters({});
    setPage(0);
  };

  // Build filter options with dynamic locations
  const filterOptions: FilterOption[] = useMemo(
    () => [
      {
        field: 'category',
        label: 'Catégorie',
        type: 'select',
        options: Object.entries(ExpenseCategoryLabels).map(([value, label]) => ({
          value,
          label,
        })),
      },
      {
        field: 'locationId',
        label: 'Emplacement',
        type: 'select',
        options:
          locationsData?.map((loc) => ({
            value: loc.id,
            label: loc.name,
          })) || [],
      },
      {
        field: 'paymentMethod',
        label: 'Paiement',
        type: 'select',
        options: Object.entries(PaymentMethodLabels).map(([value, label]) => ({
          value,
          label,
        })),
      },
      {
        field: 'startDate',
        label: 'Date début',
        type: 'date',
      },
      {
        field: 'endDate',
        label: 'Date fin',
        type: 'date',
      },
    ],
    [locationsData]
  );

  // Column definitions for ResponsiveTable
  const tableColumns: Column<Expense>[] = [
    {
      field: 'expenseDate',
      headerName: 'Date',
      render: (value) => {
        const date = new Date(value);
        return (
          <Typography variant="body2" fontWeight={500}>
            {date.toLocaleDateString('fr-FR')}
          </Typography>
        );
      },
    },
    {
      field: 'category',
      headerName: 'Catégorie',
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: `${categoryColors[value as ExpenseCategory]}20`,
              color: categoryColors[value as ExpenseCategory],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {categoryIcons[value as ExpenseCategory]}
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {ExpenseCategoryLabels[value as ExpenseCategory]}
            </Typography>
            {row.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {row.description}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      field: 'amount',
      headerName: 'Montant',
      render: (value) => (
        <Typography variant="body2" fontWeight={600} color="error.main">
          -{formatCurrency(value as number)}
        </Typography>
      ),
    },
    {
      field: 'location',
      headerName: 'Emplacement',
      render: (_, row) =>
        row.location ? (
          <Chip label={row.location.name} size="small" variant="outlined" sx={{ borderRadius: 1 }} />
        ) : (
          <Typography variant="caption" color="text.secondary">
            Organisation
          </Typography>
        ),
    },
    {
      field: 'paymentMethod',
      headerName: 'Paiement',
      render: (value) => (
        <Chip
          label={PaymentMethodLabels[value as PaymentMethod]}
          size="small"
          variant="outlined"
          sx={{ borderRadius: 1 }}
        />
      ),
    },
    {
      field: 'isRecurring',
      headerName: 'Récurrent',
      render: (value, row) =>
        value ? (
          <Tooltip title={`Fréquence: ${row.recurringFrequency || 'N/A'}`}>
            <RepeatIcon sx={{ color: palette.primary, fontSize: 20 }} />
          </Tooltip>
        ) : null,
    },
    {
      field: 'receipts',
      headerName: 'Reçus',
      render: (value) => {
        const receipts = value as Expense['receipts'];
        if (!receipts || receipts.length === 0) return null;
        return (
          <Tooltip title={`${receipts.length} reçu(s) attaché(s)`}>
            <IconButton size="small" color="primary">
              <ReceiptIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Dépenses</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Gérez les dépenses, salaires et coûts opérationnels
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onSalaryConfigClick && (
            <Button variant="outlined" onClick={onSalaryConfigClick} sx={{ borderRadius: 2 }}>
              Config. salaires
            </Button>
          )}
          {onGenerateSalariesClick && (
            <Button
              variant="outlined"
              startIcon={<PersonIcon />}
              onClick={onGenerateSalariesClick}
              sx={{ borderRadius: 2 }}
            >
              Générer salaires
            </Button>
          )}
          {onCreateClick && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateClick}
              sx={{ borderRadius: 2 }}
            >
              Nouvelle dépense
            </Button>
          )}
        </Box>
      </Box>

      <Card>
        <CardContent>
          <TableToolbar
            title="Dépenses"
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder="Rechercher par description..."
            filters={filterOptions}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            data={filteredData}
            columns={getExportColumns()}
            exportFileName="depenses"
          />

          <ResponsiveTable
            columns={tableColumns}
            rows={filteredData}
            loading={isLoading}
            emptyMessage="Aucune dépense trouvée"
            primaryField="category"
            getPrimaryText={(row) =>
              `${ExpenseCategoryLabels[row.category]} - ${formatCurrency(row.amount)}`
            }
            getSecondaryText={(row) =>
              row.description || new Date(row.expenseDate).toLocaleDateString('fr-FR')
            }
            onRowClick={onExpenseClick}
          />
          <TablePagination
            component="div"
            count={data?.total || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Lignes par page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default ExpensesList;
